import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Container, Icon } from '@pella/sash-framework';
import { useSelector } from 'react-redux';
import { usePostalCode } from '../../../../hooks/use-postal-code';
import { isValidPostal } from '../../../utilities/validations';
import { useLocation } from '../../../../context/LocationContext';
import AvailableAppointments from '../../../common/available-appointments';
import LocalShowroomCard from '../../../common/local-showroom-card';

const RTAShowroomPanel = ({ data, className }) => {
	const isMounted = useRef(null);
	const userZip = usePostalCode();
	const locations = useSelector((state) => state.locations);
	const [zip, setZip] = useState({ saved: '', input: '' });
	const [error, setError] = useState(null);
	const [branchZone, setBranchZone] = useState(null);
	const [status, setStatus] = useState('idle');
	const { setLocation } = useLocation();

	useEffect(() => setZip({ saved: userZip, input: userZip }), [userZip]);

	useEffect(() => {
		if (!isMounted.current && locations?.zone?.retail_zone) {
			isMounted.current = true;
			setBranchZone(locations.zone.retail_zone);
		}
	}, [locations?.zone?.retail_zone]);

	const onZipIconClick = () => {
		setZip({ ...zip, input: '' });
		setStatus('edit');
		document.querySelector('#rtaZip').focus();
	};

	const onInputChange = (event) => {
		setError(null);
		setZip({ ...zip, input: event.target.value });
	};

	const onInputFocus = () => {
		setZip({ ...zip, input: '' });
		setStatus('edit');
	};

	const onInputBlur = () => {
		if (!zip.input) {
			setZip({ ...zip, input: zip.saved });
			setStatus('idle');
		}
	};

	const onFormSubmit = (event) => {
		event.preventDefault();

		if (zip.input === zip.saved) {
			setStatus('idle');
			return;
		}

		if (!zip.input || !isValidPostal(zip.input)) {
			setStatus('error');
			setError('Please enter a valid zip code.');
			return;
		}

		setStatus('idle');
		setZip({ ...zip, saved: zip.input });
		setLocation(zip.input);
	};

	if (!zip?.saved || branchZone === 'NRT') return null;

	return (
		<div
			className={`rtaShowroomPanel py-8 md:py-12 ${className || ''}`}
			data-testid='rta-showroom-panel'
		>
			<Container>
				<div className='relative mb-3 flex flex-row flex-wrap justify-center md:mb-6 md:max-xl:justify-between xl:mb-8'>
					{data.heading && <h2 className='rtaShowroomPanel-heading mb-3 flex-12/12 text-pretty text-center max-md:max-w-[14ch] md:m-0 md:flex-1 md:text-left xl:text-center'>{data.heading}</h2>}

					<form
						name='rta-showroom-zip-form'
						onSubmit={onFormSubmit}
						className='flex flex-12/12 flex-row items-center justify-center md:flex-none'
						autoComplete='off'
					>
						<button
							type='button'
							className='rtaShowroomPanel-locationBtn flex-none'
							onClick={onZipIconClick}
							aria-label='Edit Zip Code'
						>
							<Icon
								name='location_on'
								className='block text-gray-50'
							/>
						</button>

						<input
							type='text'
							inputMode='numeric'
							id='rtaZip'
							name='rtaZip'
							value={zip.input}
							onFocus={onInputFocus}
							onBlur={onInputBlur}
							onChange={onInputChange}
							maxLength={7} // 7 for Canadian postal codes
							className={`min-w-[5ch] flex-none cursor-pointer py-0.5 text-center text-base font-bold leading-tight tracking-wider focus:cursor-text [&+div]:!hidden ${status === 'edit' ? '!w-[7.5ch]' : ''}`}
							style={{ width: `${zip.input.length + 0.5}ch` }}
							aria-label='Zip Code'
							autoComplete='postal-code'
						/>

						{status === 'edit' && (
							<Button
								type='submit'
								color='primary-alt'
								className='ml-2 !w-auto !px-3 !py-0 !text-sm tracking-wider [&+div]:!hidden'
								disabled={!isValidPostal(zip.input)}
							>
								Update
							</Button>
						)}
					</form>
				</div>

				{error && (
					<Alert
						className='mb-4'
						type='error'
						hideClose
					>
						{error}
					</Alert>
				)}

				<div className='grid grid-cols-1 gap-y-5 md:grid-cols-2 md:gap-x-4 md:gap-y-0 xl:gap-x-8'>
					<AvailableAppointments
						zip={zip.saved}
						className='h-full'
					/>

					<LocalShowroomCard className='h-full' />
				</div>
			</Container>
		</div>
	);
};

RTAShowroomPanel.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
};

export default RTAShowroomPanel;
